import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { apiService } from '../services/api';
import './Login.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load Poppins font dynamically
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Force Poppins font with maximum specificity
    const style = document.createElement('style');
    style.textContent = `
      .forgot-password-container *,
      .forgot-password-container *::before,
      .forgot-password-container *::after,
      .forgot-password-card *,
      .forgot-password-card *::before,
      .forgot-password-card *::after,
      .card *,
      .card *::before,
      .card *::after,
      .card-body *,
      .card-body *::before,
      .card-body *::after,
      h1, h2, h3, h4, h5, h6,
      p, span, div, button, input, label,
      .btn, .form-control, .form-label,
      .alert, .text-muted {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await apiService.forgotPassword(email);
      
      if (response.status) {
        setSuccess(true);
        setEmailSent(true);
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // Inline styles for maximum compatibility
  const poppinsStyle = {
    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 400
  };

  const headingStyle = {
    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 600
  };

  if (emailSent) {
    return (
      <div className="forgot-password-container" style={poppinsStyle}>
        <Container>
          <Row className="justify-content-center align-items-center min-vh-100">
            <Col md={6} lg={5}>
              <Card className="forgot-password-card" style={poppinsStyle}>
                <Card.Body className="p-4 text-center" style={poppinsStyle}>
                  <div className="mb-4">
                    <div className="success-icon mb-3">
                      <CheckCircle size={64} color="#28a745" />
                    </div>
                    <h2 className="mb-3" style={headingStyle}>Check Your Email</h2>
                    <p className="text-muted mb-4" style={poppinsStyle}>
                      We've sent a new password to <strong>{email}</strong>
                    </p>
                  </div>

                  <Alert variant="info" className="mb-4" style={poppinsStyle}>
                    <AlertCircle size={20} className="me-2" />
                    <strong>Important:</strong> Use the new password to log in, then change it from your profile settings for security.
                  </Alert>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      onClick={handleBackToLogin}
                      style={{...poppinsStyle, fontWeight: 500}}
                    >
                      Back to Login
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setEmailSent(false);
                        setSuccess(false);
                        setEmail('');
                      }}
                      style={poppinsStyle}
                    >
                      Try Different Email
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="forgot-password-container" style={poppinsStyle}>
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col md={6} lg={4}>
            <Card className="forgot-password-card" style={poppinsStyle}>
              <Card.Body className="p-4" style={poppinsStyle}>
                <div className="text-center mb-4">
                  <div className="forgot-password-logo mb-3">
                    <Mail size={48} />
                  </div>
                  <h2 className="mb-2" style={headingStyle}>Forgot Password?</h2>
                  <p className="text-muted" style={poppinsStyle}>
                    Enter your email address and we'll send you a new password
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3" style={poppinsStyle}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label style={poppinsStyle}>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                      style={poppinsStyle}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 mb-3"
                    disabled={isLoading || !email}
                    style={{...poppinsStyle, fontWeight: 500}}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      'Send New Password'
                    )}
                  </Button>

                  <div className="text-center">
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={handleBackToLogin}
                      style={poppinsStyle}
                    >
                      <ArrowLeft size={16} className="me-1" />
                      Back to Login
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgotPassword;
